import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, ShieldCheck, Sparkles } from "lucide-react";
import { LanguageSwitch } from "./LanguageSwitch";

export const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="bg-[#0A1128] text-white relative overflow-hidden mt-24">
      {/* Ambient blob */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#F5A623]/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#1A2B4A]/40 blur-3xl pointer-events-none" />

      {/* MASSIVE CTA */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-12 md:pb-16 relative">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-end">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F5A623]/15 text-[#F5A623] text-[10px] font-bold uppercase tracking-[0.22em] border border-[#F5A623]/25">
              <Sparkles size={11} /> {t("footer.ready_eyebrow", "Klaar om te matchen")}
            </span>
            <h2 className="font-heading text-[44px] sm:text-[64px] lg:text-[80px] font-medium leading-[0.95] tracking-tight mt-5">
              {t("home.cta.title")}
            </h2>
            <p className="text-white/60 mt-5 text-base md:text-lg max-w-xl leading-relaxed">
              {t("home.cta.subtitle")}
            </p>
          </div>
          <div className="flex flex-col gap-3 lg:items-end">
            <Link to="/onboarding" className="btn-amber-lg group" data-testid="footer-cta-start">
              {t("home.cta.primary")}
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/sem" className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors" data-testid="footer-cta-sem">
              <Sparkles size={14} className="text-[#F5A623]" /> {t("footer.try_sem", "Of vraag het aan SEM")} →
            </Link>
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-xs uppercase tracking-[0.18em] font-semibold text-white/60">
          <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-[#F5A623]" /> {t("home.cta.money_back")}</span>
          <span className="hidden md:inline-block w-1 h-1 rounded-full bg-white/20" />
          <span>{t("home.cta.independent")}</span>
          <span className="hidden md:inline-block w-1 h-1 rounded-full bg-white/20" />
          <span>{t("home.cta.advice")}</span>
          <span className="hidden md:inline-block w-1 h-1 rounded-full bg-white/20" />
          <span>AVG &amp; KvK</span>
        </div>
      </div>

      {/* Link columns */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <div className="bg-white inline-flex rounded-md px-3 py-2">
              <Logo size="sm" />
            </div>
            <p className="mt-5 text-sm text-white/55 leading-relaxed max-w-xs">
              {t("footer.tagline")}
            </p>
            <div className="mt-5">
              <LanguageSwitch />
            </div>
          </div>
          <div>
            <h4 className="eyebrow !text-white/50 mb-4">{t("footer.for_clients")}</h4>
            <ul className="space-y-2.5 text-sm text-white/75">
              <li><Link to="/onboarding" className="hover:text-[#F5A623] transition-colors">{t("footer.register_link")}</Link></li>
              <li><Link to="/zo-werkt-het" className="hover:text-[#F5A623] transition-colors">{t("footer.how_it_works")}</Link></li>
              <li><Link to="/voor-klanten" className="hover:text-[#F5A623] transition-colors">{t("footer.faq")}</Link></li>
              <li><Link to="/sem" className="hover:text-[#F5A623] transition-colors inline-flex items-center gap-1.5">SEM AI <span className="text-[8px] font-extrabold px-1 py-0.5 rounded-sm bg-[#F5A623] text-[#0A1128] uppercase">AI</span></Link></li>
            </ul>
          </div>
          <div>
            <h4 className="eyebrow !text-white/50 mb-4">{t("footer.for_firms")}</h4>
            <ul className="space-y-2.5 text-sm text-white/75">
              <li><Link to="/login" className="hover:text-[#F5A623] transition-colors">{t("footer.login_link")}</Link></li>
              <li><Link to="/voor-kantoren" className="hover:text-[#F5A623] transition-colors">{t("footer.firm_register")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="eyebrow !text-white/50 mb-4">{t("footer.company")}</h4>
            <ul className="space-y-2.5 text-sm text-white/75">
              <li><Link to="/over-ons" className="hover:text-[#F5A623] transition-colors">{t("footer.about")}</Link></li>
              <li><a href="mailto:info@beecentre.nl" className="hover:text-[#F5A623] transition-colors">info@beecentre.nl</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <p>© {new Date().getFullYear()} Beecenter. {t("footer.rights")}</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white transition-colors">{t("footer.terms")}</a>
            <a href="#" className="hover:text-white transition-colors">{t("footer.privacy")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
